import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { startOfDay } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import { DialogProps } from '@radix-ui/react-dialog';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { Form, DateTimePickerField, CheckboxField, SwitchField } from '@epoch-ui/components/Form';
import { RadioField, RadioFieldItem } from '@epoch-ui/components/Form/fields/RadioField/RadioField';
import { EventType, EventFormat } from 'types/event.types';
import {
  parseUTCDate,
  convertDateToUtc,
  formatDateInTimezone,
  guessTimezone
} from 'utils/dateTimeUtils';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { T2_DOMAIN } from 'constants/featureFlags';
import useFeatureFlag from 'hooks/useFeatureFlag';

import { TimezoneSelectorField } from '../../../../components/TimezoneSelectorField';
import { EventFormatFieldsSection } from '../../../../components/EventFormatFieldsSection';
import { useEditableEvent } from '../../../hooks/useEditableEvent';
import { duplicateEvent, DuplicateEventV2InputType } from '../../../api/duplicate-event';
import { EventDomainField } from '../../../../components/EventDomainField';

import { getDuplicateEventSchema, DuplicateEventFormValues } from './duplicate-event.schema';

interface DuplicateEventModalProps extends DialogProps {
  onDuplicate?: (eventId: number) => void;
}

export const DuplicateEventModal: React.FC<DuplicateEventModalProps> = ({
  open,
  onOpenChange,
  onDuplicate,
  ...modalProps
}) => {
  const {
    id: eventId,
    title: sourceEventTitle,
    eventType,
    timezoneCode,
    slackChannelId,
    slackChannelName,
    slackChannelAutoarchive,
    t2DomainArray: sourceEventT2DomainArray
  } = useEditableEvent();
  const { showToast } = useToaster();

  const t2DomainEnabled = useFeatureFlag(T2_DOMAIN);
  const displayConnectSlackChannel = !!slackChannelId && !slackChannelAutoarchive;
  const isSourceMultiSession = eventType === EventType.MULTI_SESSIONS;

  const DuplicateEventSchema = getDuplicateEventSchema(eventType || EventType.SINGLE, {
    t2DomainEnabled
  });

  const form = useForm<DuplicateEventFormValues>({
    resolver: zodResolver(DuplicateEventSchema),
    defaultValues: {
      duplicateType: EventType.SINGLE,
      allDay: false,
      timezoneCode: timezoneCode || guessTimezone(),
      connectSlackChannel: displayConnectSlackChannel ? true : null,
      title: sourceEventTitle ? `${sourceEventTitle} (Copy)` : '',
      format: undefined,
      virtualLink: '',
      inPersonLocation: '',
      zoomMeetingId: null,
      googleMeetingId: null,
      t2DomainArray: sourceEventT2DomainArray || undefined
    }
  });

  const convertFormDataForMutation = (
    data: DuplicateEventFormValues
  ): DuplicateEventV2InputType => {
    const {
      startDateTime,
      endDateTime,
      timezoneCode: selectedTimezone,
      duplicateType,
      allDay,
      connectSlackChannel,
      format,
      virtualLink,
      inPersonLocation,
      zoomMeetingId,
      googleMeetingId,
      t2DomainArray
    } = data;

    // Only include dates if they are provided
    const result: DuplicateEventV2InputType = {
      eventId: eventId!,
      duplicateType,
      timezoneCode: selectedTimezone,
      allDay,
      connectSlackChannel,
      t2DomainArray: t2DomainArray && t2DomainArray.length > 0 ? t2DomainArray : undefined
    };

    if (startDateTime) {
      result.datetimeScheduled = convertDateToUtc(startDateTime, selectedTimezone).toISOString();
    }

    if (endDateTime) {
      result.datetimeEnd = convertDateToUtc(endDateTime, selectedTimezone).toISOString();
    }

    // Include format fields only when duplicating multi-session as single
    if (duplicateType === EventType.SINGLE && isSourceMultiSession && format) {
      result.eventFormat = format;

      if (format === EventFormat.VIRTUAL || format === EventFormat.HYBRID) {
        result.virtualLink = virtualLink || undefined;
        result.zoomMeetingId = zoomMeetingId || undefined;
        result.googleMeetingId = googleMeetingId || undefined;
      }

      if (format === EventFormat.IN_PERSON || format === EventFormat.HYBRID) {
        result.inPersonLocation = inPersonLocation || undefined;
      }
    }

    return result;
  };

  const duplicateMutation = useMutation({
    mutationFn: duplicateEvent,
    onSuccess: (result) => {
      if (result.duplicateEventV2.ok) {
        const newEventId = result.duplicateEventV2.event.id;
        // Reset form, close modal, and call callback
        form.reset();
        onOpenChange?.(false);
        onDuplicate?.(newEventId);
      } else {
        // Handle error case
        console.error('Failed to duplicate event');
        showToast('Failed to duplicate event. Please try again.', {
          variant: ToasterVariant.ERROR
        });
      }
    },
    onError: (error) => {
      console.error('Error duplicating event:', error);
      showToast('Failed to duplicate event. Please try again.', { variant: ToasterVariant.ERROR });
    }
  });

  const handleDuplicate = (data: DuplicateEventFormValues): void => {
    // Convert form data for mutation
    const convertedData = convertFormDataForMutation(data);
    duplicateMutation.mutate(convertedData);
  };

  const duplicateType = form.watch('duplicateType');
  const allDay = form.watch('allDay');
  const watchedTimezone = form.watch('timezoneCode');

  const calendarDisabled = useMemo(() => {
    const disabledDates: any = {};

    // Disable dates in the past
    const nowUtc = parseUTCDate(new Date().toISOString());
    const nowWallClock = formatDateInTimezone(nowUtc, watchedTimezone, 'YYYY-MM-DD HH:mm:ss');
    disabledDates.before = new Date(nowWallClock.replace(' ', 'T'));

    return disabledDates;
  }, [watchedTimezone]);

  const onDuplicateTypeChange = (value: string): void => {
    const currentConnectSlackChannel = form.getValues('connectSlackChannel');
    form.reset({
      duplicateType: value as EventType,
      timezoneCode: timezoneCode || guessTimezone(),
      connectSlackChannel: currentConnectSlackChannel,
      title: sourceEventTitle ? `${sourceEventTitle} (Copy)` : '',
      format: undefined,
      virtualLink: '',
      inPersonLocation: '',
      zoomMeetingId: null,
      googleMeetingId: null,
      t2DomainArray: sourceEventT2DomainArray || undefined
    });
  };

  // Handler for when allDay checkbox changes
  const handleAllDayChange = (checked: boolean): void => {
    if (checked) {
      const currentStartDateTime = form.getValues('startDateTime');
      const currentEndDateTime = form.getValues('endDateTime');

      // Reset both start and end times to midnight if they exist
      if (currentStartDateTime) {
        form.setValue('startDateTime', startOfDay(currentStartDateTime), {
          shouldDirty: true,
          shouldValidate: true
        });
      }

      if (currentEndDateTime) {
        form.setValue('endDateTime', startOfDay(currentEndDateTime), {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    }
  };

  // Show format section when duplicating multi-session as single
  const showFormatSection = isSourceMultiSession && duplicateType === EventType.SINGLE;

  return (
    <Modal open={open} onOpenChange={onOpenChange} {...modalProps}>
      <ModalContent className="tw-max-w-[464px]" data-testid="duplicate-event-modal">
        <ModalHeader className="tw-mb-0">
          <ModalTitle>Duplicate event</ModalTitle>
        </ModalHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleDuplicate, (errors) => {
              console.error(errors);
            })}
            className="tw-space-y-6"
          >
            <div className="tw-mt-5">
              <RadioField
                className="tw-mt-3"
                name="duplicateType"
                label="Duplicate as:"
                onValueChange={onDuplicateTypeChange}
              >
                <RadioFieldItem value={EventType.SINGLE} className="tw-flex tw-items-center">
                  <span>Single event</span>
                </RadioFieldItem>
                <RadioFieldItem
                  value={EventType.MULTI_SESSIONS}
                  className="tw-flex tw-items-center"
                >
                  <span>Multi-session event</span>
                </RadioFieldItem>
              </RadioField>

              {duplicateType === EventType.SINGLE && (
                <div>
                  <DateTimePickerField
                    name="startDateTime"
                    label="Start time"
                    className="tw-my-4 tw-w-[416px]"
                    allDay={allDay}
                  >
                    <DateTimePickerField.DateInput
                      className="tw-mr-1 tw-w-[150px]"
                      placeholder="Date"
                      disabled={calendarDisabled}
                    />
                    <DateTimePickerField.Time
                      className="tw-mr-1 tw-w-[134px]"
                      interval={5}
                      placeholder="Time"
                    />
                    <TimezoneSelectorField
                      name="timezoneCode"
                      className="tw-w-[108px]"
                      valueFormat="abbreviation"
                    />
                  </DateTimePickerField>

                  <DateTimePickerField
                    name="endDateTime"
                    label="End time"
                    allDay={allDay}
                    className="tw-mb-4 tw-w-[416px]"
                  >
                    <DateTimePickerField.DateInput
                      className="tw-mr-1 tw-w-[150px]"
                      placeholder="Date"
                      disabled={calendarDisabled}
                    />
                    <DateTimePickerField.Time
                      className="tw-mr-1 tw-w-[134px]"
                      interval={5}
                      placeholder="Time"
                    />
                    <TimezoneSelectorField
                      readOnly
                      name="timezoneCode"
                      className="tw-w-[108px]"
                      valueFormat="abbreviation"
                    />
                  </DateTimePickerField>

                  <CheckboxField
                    name="allDay"
                    label="All day"
                    className="tw-my-4"
                    onCheckedChange={handleAllDayChange}
                  />

                  {showFormatSection && <EventFormatFieldsSection />}
                </div>
              )}

              {duplicateType === EventType.MULTI_SESSIONS &&
                eventType === EventType.MULTI_SESSIONS && (
                  <DateTimePickerField name="startDateTime" label="Start time" className="tw-my-4">
                    <DateTimePickerField.DateInput
                      className="tw-mr-1 tw-w-full"
                      placeholder="Date"
                      disabled={calendarDisabled}
                    />
                  </DateTimePickerField>
                )}
            </div>

            {displayConnectSlackChannel && (
              <SwitchField
                name="connectSlackChannel"
                label={`Connect existing Slack channel to new event`}
                description={`#${slackChannelName}`}
                className="tw-mb-4 tw-flex-row-reverse tw-justify-between"
              />
            )}

            {t2DomainEnabled && <EventDomainField name="t2DomainArray" />}

            <ModalFooter className="!tw-mt-5">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                className="tw-w-full tw-rounded-lg"
                loading={duplicateMutation.isPending}
                disabled={duplicateMutation.isPending}
              >
                Duplicate
              </Button>
            </ModalFooter>
          </form>
        </Form>
      </ModalContent>
    </Modal>
  );
};
